import { ReactElement, ReactNode } from 'react';
import { Box } from '@mui/material';
import { SxProps } from '@mui/material/styles';
import { Button } from 'framework/ui/mui-builder';
import { ShowSection } from 'framework/ui/layout';

interface ApprovalLayoutProps {
  title?: string | ReactElement;
  titleVariant?: 'h2' | 'h4';
  children: ReactNode;
  onApprove?: () => void;
  onReject?: () => void;
  approveLabel?: string;
  rejectLabel?: string;
  rejectColor?: 'primary' | 'error';
  isSubmitting?: boolean;
  actions?: ReactNode;
}

const sectionSx: SxProps = {
  position: 'relative',
  '&.section-details__wrapper': {
    backgroundColor: 'background.paper',
    borderRadius: 1,
  },
};

const footerSx = { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 2, px: 2 } as const;

const ApprovalLayout = ({
  title,
  titleVariant,
  children,
  onApprove,
  onReject,
  approveLabel = 'Approve',
  rejectLabel = 'Reject',
  rejectColor = 'primary',
  isSubmitting = false,
  actions,
}: ApprovalLayoutProps) => {
  const defaultButtons =
    onApprove || onReject ? (
      <>
        {onReject && (
          <Button variant="outlined" color={rejectColor} onClick={onReject} disabled={isSubmitting}>
            {rejectLabel}
          </Button>
        )}
        {onApprove && (
          <Button variant="contained" color="primary" onClick={onApprove} disabled={isSubmitting}>
            {approveLabel}
          </Button>
        )}
      </>
    ) : null;

  const footer = actions ?? defaultButtons;

  return (
    <ShowSection title={title} titleVariant={titleVariant} sx={sectionSx}>
      <Box sx={{ px: 2 }}>{children}</Box>
      {footer && <Box sx={footerSx}>{footer}</Box>}
    </ShowSection>
  );
};

export default ApprovalLayout;
